import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike {
  error: string;
  message?: string;
}

/**
 * Minimal structural type for the Web Speech API. The DOM lib does not ship
 * these types, and the API is vendor-prefixed, so we describe the subset we use
 * rather than reaching for `any`.
 */
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
}

interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function friendlyError(error: string): string {
  switch (error) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone access was blocked. Allow it in your browser settings and try again.";
    case "no-speech":
      return "No speech detected. Try again and speak clearly.";
    case "audio-capture":
      return "No microphone was found.";
    case "network":
      return "Speech recognition lost its network connection.";
    default:
      return "Speech recognition stopped unexpectedly.";
  }
}

export interface SpeechToTextOptions {
  lang?: string;
  /** Called with each finalized phrase as it is recognized. */
  onFinal: (text: string) => void;
  /** Called with the in-progress (interim) phrase, if supplied. */
  onInterim?: (text: string) => void;
  onError?: (message: string) => void;
}

export interface SpeechToText {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
}

/**
 * Dictation via the browser's Web Speech API. `supported` is false on browsers
 * that lack it (e.g. Firefox), so callers can hide the mic affordance entirely.
 */
export function useSpeechToText(options: SpeechToTextOptions): SpeechToText {
  const supported = useMemo(() => getRecognitionCtor() !== null, []);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    recognitionRef.current?.abort();

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = optionsRef.current.lang ?? "en-US";
    recognition.onstart = () => setListening(true);
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const chunk = result[0]?.transcript ?? "";
        if (result.isFinal) {
          const finalText = chunk.trim();
          if (finalText) optionsRef.current.onFinal(finalText);
        } else {
          interim += chunk;
        }
      }
      if (interim.trim()) optionsRef.current.onInterim?.(interim.trim());
    };
    recognition.onerror = (event) => {
      if (event.error !== "aborted") optionsRef.current.onError?.(friendlyError(event.error));
      setListening(false);
    };
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      setListening(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  return { supported, listening, start, stop };
}
